import { useMemo } from "react";

import DrawingsPanel from "@/features/drawings/panel/DrawingsPanel";
import type { DrawingsPanelOps, PanelItem } from "@/features/drawings/panel/types";
import {
  createItemForProjectDrawing,
  createItemsForProjectDrawings,
  deleteProjectDrawing,
  deleteProjectDrawings,
  importProjectDrawings,
  linkProjectDrawing,
  matchProjectDrawings,
  rebuildProjectDrawingThumbnails,
  rereadProjectDrawingNumbers,
  stampProjectDrawingQr,
  stampProjectDrawingsQr,
  updateProjectDrawing,
  type ProjectItem,
} from "./api";
import { projectDrawingsQuery } from "./drawingsQuery";

/**
 * 案件詳細の「図面」タブ。
 *
 * 中身は見積と共通（[[DrawingsPanel]]）。ここは**案件の経路と文言を与えるだけ**の
 * 包み。扱うのは受注後に現場へ届いた追図・差し替えで、取り込み方も突き合わせ方も
 * 見積と同じ。
 *
 * **見積から引き継いだ図面は、この画面からは動かせない**（紐付けの変更も削除も
 * API が 409 を返す）。図面の実体は見積と案件で同じ 1 行を共有していて、追従は
 * 見積 → 案件の一方向だから。カードには出どころの見積番号を出し、ダイアログでも
 * 操作を伏せて理由を書く。
 *
 * 明細側の作業画面（工程ごとの見積）は案件には無いので、カード下端のボタンは
 * 出さない（`openEditor` を渡さない）。
 *
 * **QR の押印はこの画面にだけある**（`stampQr` を渡す）。QR は「どの案件の
 * どの明細のどの図面か」を指すので、工番の無い見積の画面では押せない。
 */
export default function ProjectDrawingsPanel({
  projectId,
  canWrite,
  items,
}: {
  projectId: number;
  canWrite: boolean;
  /** 紐付け先の候補にする案件明細。 */
  items: ProjectItem[];
}) {
  const ops = useMemo<DrawingsPanelOps>(
    () => ({
      queryKey: projectDrawingsQuery(projectId).queryKey,
      // 紐付けが変わると明細側の図面リンク・版数の表示も変わる。
      invalidateKeys: [["project-items", projectId]],
      eventsPath: `/projects/${projectId}/drawings/events`,

      list: () => projectDrawingsQuery(projectId).queryFn(),
      importFile: (file) => importProjectDrawings(projectId, file),
      saveNumber: (drawingId, drawingNumber) =>
        updateProjectDrawing(projectId, drawingId, { drawing_number: drawingNumber }),
      link: (drawingId, itemId) => linkProjectDrawing(projectId, drawingId, itemId),
      createItem: (drawingId) => createItemForProjectDrawing(projectId, drawingId),
      createItems: (drawingIds) => createItemsForProjectDrawings(projectId, drawingIds),
      match: async (drawingIds, dryRun) => {
        const res = await matchProjectDrawings(projectId, drawingIds, dryRun);
        return { results: res.results };
      },
      remove: (drawingId) => deleteProjectDrawing(projectId, drawingId),
      removeMany: (drawingIds) => deleteProjectDrawings(projectId, drawingIds),
      rebuildThumbnails: () => rebuildProjectDrawingThumbnails(projectId),
      rereadNumbers: (drawingIds) => rereadProjectDrawingNumbers(projectId, drawingIds),
      // QR は案件の画面にだけ出す（指すのが案件・明細・図面・版のため）。
      stampQr: (drawingId, area, scalePercent) =>
        stampProjectDrawingQr(projectId, drawingId, area, scalePercent),
      stampQrMany: (drawingIds, area, scalePercent) =>
        stampProjectDrawingsQr(projectId, drawingIds, area, scalePercent),

      matchDescription: "案件明細の図番と突き合わせます。",
      linkHint: "まだ図面が付いていない明細だけが候補に出ます。",
      createItemHint:
        "注文に載っていない図面のための操作です。品名は図番で代用し、数量は 1、納期は案件の納期で起こします。",
    }),
    [projectId],
  );

  const panelItems = useMemo<PanelItem[]>(
    () =>
      items.map((item) => ({
        item_id: item.project_item_id,
        sequence: item.sequence,
        item_name: item.item_name,
        drawing_number: item.drawing_number,
      })),
    [items],
  );

  return <DrawingsPanel ops={ops} canWrite={canWrite} items={panelItems} />;
}
